/**
 * Right-side panel that edits a selected step.
 *
 * Editable: title / description / duration / automation kind/id.
 * Pickers : KPIs (multi-select with relationship), accountable seats (multi-select with RACI role).
 *
 * Saves with debounced autosave on text fields and immediate save on
 * checkbox-style picker changes. The DAO endpoints used are:
 *   PATCH /v4/processes/:id/steps/:stepId           (text/automation)
 *   PUT   /v4/processes/:id/steps/:stepId/kpis      (replace KPI links)
 *   PUT   /v4/processes/:id/steps/:stepId/seats     (replace step seats)
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, X, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { processesApi } from '../api';
import { useSetStepKpis, useSetStepSeats } from '../hooks';
import type { ProcessStep, StepKpiRow, StepSeatRow, KpiRelationship, RaciRole } from '../types';
import { useKpiDefinitions } from '@/features/kpis/hooks/useKpis';
import { useSeats } from '@/features/accountability/api/seats';
import { useQueryClient } from '@tanstack/react-query';
import { processesKeys } from '../api';
import { toast } from 'sonner';

const RACI: RaciRole[] = ['accountable', 'responsible', 'consulted', 'informed'];
const KPI_REL: KpiRelationship[] = ['measures', 'gates', 'triggers'];

interface StepPanelProps {
  processId: string;
  step: ProcessStep;
  stepKpis: StepKpiRow[];
  stepSeats: StepSeatRow[];
}

export function StepPanel({ processId, step, stepKpis, stepSeats }: StepPanelProps) {
  const qc = useQueryClient();

  // Local mirrors for text edits.
  const [title, setTitle] = useState(step.title);
  const [description, setDescription] = useState(step.description || '');
  const [duration, setDuration] = useState<string>(step.duration_estimate_minutes?.toString() || '');
  const [automationKind, setAutomationKind] = useState<string>(
    typeof step.automation?.kind === 'string' ? (step.automation.kind as string) : 'manual',
  );
  const [automationId, setAutomationId] = useState<string>(
    typeof step.automation?.id === 'string' ? (step.automation.id as string) : '',
  );
  const [savingMeta, setSavingMeta] = useState(false);

  // Reset when selection changes.
  useEffect(() => {
    setTitle(step.title);
    setDescription(step.description || '');
    setDuration(step.duration_estimate_minutes?.toString() || '');
    setAutomationKind(typeof step.automation?.kind === 'string' ? (step.automation.kind as string) : 'manual');
    setAutomationId(typeof step.automation?.id === 'string' ? (step.automation.id as string) : '');
  }, [step.id]);

  const dirtyMeta = (
    title !== step.title
    || (description || '') !== (step.description || '')
    || (duration || '') !== (step.duration_estimate_minutes?.toString() || '')
    || automationKind !== (typeof step.automation?.kind === 'string' ? (step.automation.kind as string) : 'manual')
    || automationId !== (typeof step.automation?.id === 'string' ? (step.automation.id as string) : '')
  );

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await processesApi.updateStep(processId, step.id, {
        title,
        description: description || null,
        duration_estimate_minutes: duration ? parseInt(duration, 10) : null,
        automation: { kind: automationKind, ...(automationId ? { id: automationId } : {}) },
      } as Partial<ProcessStep>);
      qc.invalidateQueries({ queryKey: processesKeys.graph(processId) });
      toast.success('Step saved');
    } catch (e) {
      toast.error('Failed to save step', { description: (e as Error).message });
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <div className="space-y-4 text-sm">
      <div>
        <Label htmlFor="step-title" className="text-xs">Title</Label>
        <Input id="step-title" value={title} onChange={(e) => setTitle(e.target.value)} className="h-8" />
      </div>
      <div className="text-xs text-muted-foreground capitalize">Type: {step.node_type}</div>
      <div>
        <Label htmlFor="step-desc" className="text-xs">Description</Label>
        <Textarea id="step-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="text-xs" />
      </div>
      <div>
        <Label htmlFor="step-dur" className="text-xs">Estimated duration (minutes)</Label>
        <Input id="step-dur" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="h-8" placeholder="—" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Automation</Label>
        <Select value={automationKind} onValueChange={setAutomationKind}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="mastra_workflow">Mastra workflow</SelectItem>
            <SelectItem value="mastra_agent">Mastra agent</SelectItem>
            <SelectItem value="webhook">Webhook</SelectItem>
            <SelectItem value="cron">Cron</SelectItem>
          </SelectContent>
        </Select>
        {automationKind !== 'manual' && (
          <Input
            value={automationId}
            onChange={(e) => setAutomationId(e.target.value)}
            placeholder="ID / slug (e.g. acc-accounts-receivable)"
            className="h-8 text-xs mt-1"
          />
        )}
      </div>

      <Button size="sm" onClick={saveMeta} disabled={!dirtyMeta || savingMeta} className="w-full">
        {savingMeta && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
        Save step details
      </Button>

      <KpiPicker processId={processId} stepId={step.id} stepKpis={stepKpis} />
      <SeatPicker processId={processId} stepId={step.id} stepSeats={stepSeats} />
    </div>
  );
}

// =============================================================================
// KPI picker — multi-select with relationship dropdown per linked KPI.
// =============================================================================
function KpiPicker({ processId, stepId, stepKpis }: { processId: string; stepId: string; stepKpis: StepKpiRow[] }) {
  const { data: kpisResponse } = useKpiDefinitions({ is_active: true });
  const allKpis = kpisResponse?.definitions || [];
  const setMutation = useSetStepKpis(processId);

  const [open, setOpen] = useState(false);

  // Local copy of the link list so the user can change relationship + add/remove
  // without an immediate fetch. Persisted on Save.
  const [items, setItems] = useState<Array<{ kpiId: string; relationship: KpiRelationship; name: string }>>(
    stepKpis.map((k) => ({ kpiId: k.kpi_id, relationship: k.relationship, name: k.kpi_name })),
  );
  useEffect(() => {
    setItems(stepKpis.map((k) => ({ kpiId: k.kpi_id, relationship: k.relationship, name: k.kpi_name })));
  }, [stepId, stepKpis.length]);

  const linkedIds = useMemo(() => new Set(items.map((i) => i.kpiId)), [items]);

  const toggle = (kpiId: string, name: string) => {
    setItems((prev) => prev.find((p) => p.kpiId === kpiId)
      ? prev.filter((p) => p.kpiId !== kpiId)
      : [...prev, { kpiId, name, relationship: 'measures' as KpiRelationship }]);
  };

  const setRelationship = (kpiId: string, relationship: KpiRelationship) => {
    setItems((prev) => prev.map((p) => p.kpiId === kpiId ? { ...p, relationship } : p));
  };

  const dirty = useMemo(() => {
    if (items.length !== stepKpis.length) return true;
    const map = new Map(stepKpis.map((k) => [k.kpi_id, k.relationship]));
    return items.some((i) => map.get(i.kpiId) !== i.relationship);
  }, [items, stepKpis]);

  const save = async () => {
    await setMutation.mutateAsync({ stepId, items: items.map((i) => ({ kpiId: i.kpiId, relationship: i.relationship })) });
  };

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase font-medium text-muted-foreground">KPIs ({items.length})</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs">
              Add <ChevronsUpDown className="h-3 w-3 ml-1 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="end">
            <Command>
              <CommandInput placeholder="Search KPIs…" className="h-8 text-xs" />
              <CommandList>
                <CommandEmpty className="py-2 text-xs text-center text-muted-foreground">No KPIs.</CommandEmpty>
                <CommandGroup className="max-h-48 overflow-y-auto">
                  {allKpis.map((k) => (
                    <CommandItem key={k.id} value={k.name} onSelect={() => toggle(k.id, k.name)} className="text-xs">
                      <div className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        linkedIds.has(k.id) ? 'bg-primary text-primary-foreground' : 'opacity-50',
                      )}>
                        {linkedIds.has(k.id) && <Check className="h-3 w-3" />}
                      </div>
                      {k.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {items.length === 0 && (
        <div className="text-xs text-muted-foreground italic">No KPIs linked. Click "Add" to pick.</div>
      )}

      {items.map((it) => (
        <div key={it.kpiId} className="flex items-center gap-1">
          <Select value={it.relationship} onValueChange={(v) => setRelationship(it.kpiId, v as KpiRelationship)}>
            <SelectTrigger className="h-7 w-[100px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {KPI_REL.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs flex-1 truncate" title={it.name}>{it.name}</span>
          <button onClick={() => toggle(it.kpiId, it.name)} className="text-muted-foreground hover:text-destructive">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      {dirty && (
        <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={save} disabled={setMutation.isPending}>
          {setMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
          Save KPI links
        </Button>
      )}
    </div>
  );
}

// =============================================================================
// Seat picker — RACI assignment for the step.
// =============================================================================
function SeatPicker({ processId, stepId, stepSeats }: { processId: string; stepId: string; stepSeats: StepSeatRow[] }) {
  const { data: seatsData } = useSeats();
  const allSeats = seatsData?.seats || [];
  const setMutation = useSetStepSeats(processId);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Array<{ seatId: string; role: RaciRole; title: string }>>(
    stepSeats.map((s) => ({ seatId: s.seat_id, role: s.role, title: s.seat_title })),
  );
  useEffect(() => {
    setItems(stepSeats.map((s) => ({ seatId: s.seat_id, role: s.role, title: s.seat_title })));
  }, [stepId, stepSeats.length]);

  const toggle = (seatId: string, title: string, role: RaciRole = 'accountable') => {
    const key = `${seatId}-${role}`;
    setItems((prev) => prev.find((p) => `${p.seatId}-${p.role}` === key)
      ? prev.filter((p) => `${p.seatId}-${p.role}` !== key)
      : [...prev, { seatId, role, title }]);
  };

  const setRole = (seatId: string, prevRole: RaciRole, nextRole: RaciRole) => {
    setItems((prev) => prev.map((p) => p.seatId === seatId && p.role === prevRole ? { ...p, role: nextRole } : p));
  };

  const dirty = useMemo(() => {
    if (items.length !== stepSeats.length) return true;
    const map = new Map(stepSeats.map((s) => [`${s.seat_id}-${s.role}`, true]));
    return items.some((i) => !map.get(`${i.seatId}-${i.role}`));
  }, [items, stepSeats]);

  const save = async () => {
    await setMutation.mutateAsync({ stepId, items: items.map((i) => ({ seatId: i.seatId, role: i.role })) });
  };

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase font-medium text-muted-foreground">Seats / RACI ({items.length})</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs">
              Add <ChevronsUpDown className="h-3 w-3 ml-1 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="end">
            <Command>
              <CommandInput placeholder="Search seats…" className="h-8 text-xs" />
              <CommandList>
                <CommandEmpty className="py-2 text-xs text-center text-muted-foreground">No seats.</CommandEmpty>
                <CommandGroup className="max-h-48 overflow-y-auto">
                  {allSeats.map((s) => {
                    const isLinked = !!items.find((p) => p.seatId === s.id);
                    return (
                      <CommandItem key={s.id} value={s.title} onSelect={() => toggle(s.id, s.title)} className="text-xs">
                        <div className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isLinked ? 'bg-primary text-primary-foreground' : 'opacity-50',
                        )}>
                          {isLinked && <Check className="h-3 w-3" />}
                        </div>
                        {s.title}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {items.length === 0 && (
        <div className="text-xs text-muted-foreground italic">No seats assigned. Click "Add" to pick.</div>
      )}

      {items.map((it) => (
        <div key={`${it.seatId}-${it.role}`} className="flex items-center gap-1">
          <Select value={it.role} onValueChange={(v) => setRole(it.seatId, it.role, v as RaciRole)}>
            <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RACI.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs flex-1 truncate" title={it.title}>{it.title}</span>
          <button onClick={() => toggle(it.seatId, it.title, it.role)} className="text-muted-foreground hover:text-destructive">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      {dirty && (
        <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={save} disabled={setMutation.isPending}>
          {setMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
          Save seat assignments
        </Button>
      )}
    </div>
  );
}

// Re-export helper Badge so the page panel can use it for placeholder owners.
export { Badge };
